import React, { useMemo } from 'react';
import { ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { useMount } from '@shared/utils/hooks/useMount';
import { AllyTransferPromo } from '@shared/components/TransferToBankDialog/AllyTransferPromo';
import { RevolutTransferPromo } from '@shared/components/TransferToBankDialog/RevolutTransferPromo';
import { useActiveTransferPartners } from '@shared/components/TransferToBankDialog/useActiveTransferPartners';
import type { PayoutAccountInfo } from './PayoutDialog.types';

type ConnectBankStepProps = Readonly<{
  payoutAccountInfo: PayoutAccountInfo;
  onConnectBank: () => void;
  onBankPromoViewed?: (partner: string) => void;
  onBankPromoInteracted?: (cta: string, partner: string) => void;
}>;

export const useIsAllyPromoVisible = (payoutAccountInfo: PayoutAccountInfo): boolean => {
  const { activeTransferConfig, activeRepeatConfig } = useActiveTransferPartners();
  const isReadyToUse = payoutAccountInfo.status === 'readyToUse';
  const isPayoutAllowed = (payoutAccountInfo.balance?.available?.valueInMinorUnits ?? 0) > 0;
  return isReadyToUse ? activeRepeatConfig?.partnerId === 'ally' && isPayoutAllowed : activeTransferConfig?.partnerId === 'ally';
};

export const ConnectBankStep: React.FC<ConnectBankStepProps> = ({ payoutAccountInfo, onConnectBank, onBankPromoViewed, onBankPromoInteracted }) => {
  const { tq } = useTranslation('cashFundsAdmin');
  const { formatCurrency } = useCurrencyFormatter();
  const { activeTransferConfig, activeRepeatConfig } = useActiveTransferPartners();

  const isReadyToUse = payoutAccountInfo.status === 'readyToUse';
  const available = payoutAccountInfo.balance?.available;
  const currencyCode = available?.currencyCode ?? payoutAccountInfo.currencyCode;
  const isPayoutAllowed = (available?.valueInMinorUnits ?? 0) > 0;

  const showAllyRepeatPromo = isReadyToUse && activeRepeatConfig?.partnerId === 'ally' && isPayoutAllowed;
  const showRevolutRepeatPromo = isReadyToUse && activeRepeatConfig?.partnerId === 'revolut' && isPayoutAllowed;
  const showAllyInitialPromo = !isReadyToUse && activeTransferConfig?.partnerId === 'ally';
  const showRevolutInitialPromo = !isReadyToUse && activeTransferConfig?.partnerId === 'revolut';

  const formattedBalance = useMemo(
    () => formatCurrency({ priceFloatingPointDecimalString: (available?.valueInMajorUnits ?? 0).toString(), priceCurrencyCode: currencyCode, formatForm: 'auto' }),
    [formatCurrency, available, currencyCode],
  );

  useMount(() => {
    if (showAllyRepeatPromo || showAllyInitialPromo) onBankPromoViewed?.('ally');
    else if (showRevolutRepeatPromo || showRevolutInitialPromo) onBankPromoViewed?.('revolut');
  });

  if (showAllyRepeatPromo && activeRepeatConfig) {
    return (
      <AllyTransferPromo
        config={activeRepeatConfig}
        amountFormatted={formattedBalance}
        onSkip={onConnectBank}
        onBankPromoInteracted={onBankPromoInteracted}
      />
    );
  }

  if (showRevolutRepeatPromo && activeRepeatConfig) {
    return (
      <RevolutTransferPromo
        config={activeRepeatConfig}
        amountFormatted={formattedBalance}
        onSkip={onConnectBank}
        autoAdvanceOnCta
        onBankPromoInteracted={onBankPromoInteracted}
      />
    );
  }

  if (showRevolutInitialPromo && activeTransferConfig) {
    return (
      <RevolutTransferPromo
        config={activeTransferConfig}
        amountFormatted={formattedBalance}
        onSkip={onConnectBank}
        onBankPromoInteracted={onBankPromoInteracted}
      />
    );
  }

  if (showAllyInitialPromo && activeTransferConfig) {
    return (
      <AllyTransferPromo
        config={activeTransferConfig}
        amountFormatted={formattedBalance}
        onSkip={onConnectBank}
        onBankPromoInteracted={onBankPromoInteracted}
      />
    );
  }

  return (
    <Flex
      flexDirection="column"
      flex={1}
    >
      <Flex
        justifyContent="center"
        marginBottom={6}
      >
        <TextV2 typographyVariant="display1">{tq`Transfer to Bank`}</TextV2>
      </Flex>

      <Flex
        flexDirection="column"
        alignItems="center"
        flex={1}
        justifyContent="center"
        gap={1}
      >
        <TextV2
          typographyVariant="display2"
          color="mono14"
        >
          {formattedBalance}
        </TextV2>
        <TextV2
          typographyVariant="hed1"
          color="mono14"
        >
          {tq`Transferrable Balance`}
        </TextV2>
      </Flex>

      <ButtonV2
        size="lg"
        shape="rounded"
        intent="neutral"
        fullWidth
        marginTop={6}
        onClick={onConnectBank}
      >
        {tq`Connect Bank Account`}
      </ButtonV2>
    </Flex>
  );
};
